import React, { useState, useEffect, useRef } from "react";
import { MicOutline, Stop, RefreshOutline, CheckmarkCircle, VolumeHighOutline } from "react-ionicons";

const PronunciationPractice = (props) => {
    const { data, markedAsDone } = props;
    const moduloactual = data.moduloactual;

    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState("");
    const [score, setScore] = useState(moduloactual.hecho ? 100 : 0);
    const [hasPassed, setHasPassed] = useState(moduloactual.hecho === true || moduloactual.hecho === 1);
    const [recognitionError, setRecognitionError] = useState("");
    const [phonetics, setPhonetics] = useState({});

    useEffect(() => {
        // Pre-load voices on component mount
        if (typeof window !== "undefined" && window.speechSynthesis) {
            window.speechSynthesis.getVoices();
            const handleVoicesChanged = () => {
                window.speechSynthesis.getVoices();
            };
            window.speechSynthesis.addEventListener("voiceschanged", handleVoicesChanged);
            return () => {
                window.speechSynthesis.removeEventListener("voiceschanged", handleVoicesChanged);
            };
        }
    }, []);

    const recognitionRef = useRef(null);

    // Extract target text preserving newlines/paragraphs
    const cleanWord = (word) => word.toLowerCase().replace(/[^a-z0-9]/g, "");

    const { rawTargetText, targetWords, targetWordsClean, structuredLines } = React.useMemo(() => {
        const html = moduloactual.cursos_item.descripcion || "";
        let formatted = html
            .replace(/<p[^>]*>/gi, "")
            .replace(/<\/p>/gi, "\n")
            .replace(/<div[^>]*>/gi, "")
            .replace(/<\/div>/gi, "\n")
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/&nbsp;/g, " ");
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = formatted;
        const text = tempDiv.textContent || tempDiv.innerText || "";
        
        const wordsList = [];
        const cleanWordsList = [];
        const lines = text.split("\n").map((line) => {
            const trimmed = line.trim();
            if (trimmed.length === 0) {
                return { type: "empty" };
            }
            const words = trimmed.split(/\s+/).filter((w) => w.length > 0);
            const lineWords = words.map((word) => {
                const clean = cleanWord(word);
                const flatIndex = wordsList.length;
                wordsList.push(word);
                cleanWordsList.push(clean);
                return {
                    word,
                    clean,
                    flatIndex
                };
            });
            return {
                type: "line",
                words: lineWords
            };
        });

        return {
            rawTargetText: text,
            targetWords: wordsList,
            targetWordsClean: cleanWordsList,
            structuredLines: lines
        };
    }, [moduloactual.cursos_item.descripcion]);

    useEffect(() => {
        const fetchPhonetics = async () => {
            for (const word of targetWordsClean) {
                try {
                    let phoneticText = "";
                    try {
                        const res = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${word}`);
                        if (res.ok) {
                            const json = await res.json();
                            if (json && json.length > 0) {
                                phoneticText = json[0].phonetic;
                                if (!phoneticText && json[0].phonetics) {
                                    const p = json[0].phonetics.find(x => x.text);
                                    if (p) phoneticText = p.text;
                                }
                            }
                        }
                    } catch (e1) {
                        // ignore and try fallback
                    }

                    if (!phoneticText) {
                        const res2 = await fetch(`https://rhymebrain.com/talk?function=getWordInfo&word=${word}`);
                        if (res2.ok) {
                            const json2 = await res2.json();
                            if (json2 && json2.ipa) {
                                phoneticText = `/${json2.ipa}/`;
                            }
                        }
                    }

                    if (phoneticText) {
                        setPhonetics(prev => ({ ...prev, [word]: phoneticText }));
                    }
                } catch (e) {
                    console.log("Phonetics arr", e);
                }
            }
        };
        fetchPhonetics();
    }, [rawTargetText]);

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setRecognitionError("Tu navegador no soporta el reconocimiento de voz. Te recomendamos usar Google Chrome.");
            return;
        }

        const recognition = new SpeechRecognition();
        const langCode = moduloactual.cursos_item.html || "en-US";
        recognition.lang = langCode;
        recognition.interimResults = true;
        recognition.continuous = true;

        recognition.onresult = (event) => {
            let finalTranscript = "";
            let interimTranscript = "";
            for (let i = 0; i < event.results.length; i++) {
                if (event.results[i].isFinal) {
                    finalTranscript += event.results[i][0].transcript + " ";
                } else {
                    interimTranscript += event.results[i][0].transcript + " ";
                }
            }
            setTranscript(finalTranscript + interimTranscript);
        };

        recognition.onerror = (event) => {
            console.error("Speech recognition error", event.error);
            setIsListening(false);
        };

        recognition.onend = () => {
            setIsListening(false);
        };

        recognitionRef.current = recognition;

        // Cleanup
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const toggleListening = () => {
        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
        } else {
            setTranscript("");
            setScore(0);
            setHasPassed(false);
            setRecognitionError("");
            try {
                recognitionRef.current?.start();
                setIsListening(true);
            } catch (err) {
                console.error(err);
            }
        }
    };

    const resetPractice = () => {
        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
        }
        setTranscript("");
        setScore(0);
        setHasPassed(false);
    };

    useEffect(() => {
        if (!targetWordsClean.length || transcript.length === 0) return;

        const spokenWords = transcript.split(/\s+/).filter((w) => w.length > 0).map(cleanWord);
        
        let matches = 0;
        let spokenIndex = 0;

        for (let i = 0; i < targetWordsClean.length; i++) {
            let found = false;
            for (let j = spokenIndex; j < spokenWords.length; j++) {
                if (targetWordsClean[i] === spokenWords[j]) {
                    matches++;
                    spokenIndex = j + 1;
                    found = true;
                    break;
                }
            }
        }

        const currentScore = Math.floor((matches / targetWordsClean.length) * 100);
        setScore(currentScore);

        if (currentScore >= 80 && !hasPassed) {
            setHasPassed(true);
            if (!(moduloactual.hecho === true || moduloactual.hecho === 1 || moduloactual.hecho === "1")) {
                setTimeout(() => {
                     markedAsDone(moduloactual.id);
                }, 1500);
            }
        }
    }, [transcript]);

    const renderTextWithFeedback = () => {
        const spokenWords = transcript.split(/\s+/).filter((w) => w.length > 0).map(cleanWord);
        let spokenIndex = 0;

        return structuredLines.map((line, lineIndex) => {
            if (line.type === "empty") {
                return <div key={`empty-${lineIndex}`} style={{ height: "1.5rem" }} />;
            }

            return (
                <div key={`line-${lineIndex}`} className="d-flex flex-row flex-wrap justify-content-center align-items-center mb-3">
                    {line.words.map((wordObj) => {
                        const { word, clean, flatIndex } = wordObj;
                        let isMatched = false;

                        for (let j = spokenIndex; j < spokenWords.length; j++) {
                            if (clean === spokenWords[j]) {
                                isMatched = true;
                                spokenIndex = j + 1;
                                break;
                            }
                        }

                        let colorStatus = "text-secondary"; // Default unread
                        if (transcript.length > 0) {
                            colorStatus = isMatched ? "text-success font-weight-bold" : "text-danger";
                        }

                        return (
                            <div key={flatIndex} className="d-inline-flex flex-column align-items-center justify-content-center mr-2 mb-2" style={{ minWidth: "40px" }}>
                                <span className={`${colorStatus}`} style={{ fontSize: "2.2rem", transition: "color 0.3s" }}>
                                    {word}
                                </span>
                                {phonetics[clean] ? (
                                    <small className="text-secondary" style={{ fontSize: "1rem", opacity: 0.8 }}>
                                        {phonetics[clean]}
                                    </small>
                                ) : (
                                    <small className="text-secondary" style={{ fontSize: "1rem", opacity: 0, userSelect: 'none' }}>
                                        ...
                                    </small>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        });
    };

    return (
        <div className="card shadow-sm mb-4">
            <div className="card-body text-center p-4">
                <h4 className="mb-4">Práctica de Pronunciación</h4>
                
                {recognitionError && (
                    <div className="alert alert-danger">{recognitionError}</div>
                )}
                
                <div className="mb-4 p-4 bg-light rounded" style={{ minHeight: "150px", lineHeight: "2" }}>
                    {renderTextWithFeedback()}
                </div>

                <div className="d-flex justify-content-center align-items-center gap-3">
                    <button
                        className="btn btn-outline-info rounded-circle p-3 d-flex justify-content-center align-items-center mr-3"
                        onClick={() => {
                            if (!('speechSynthesis' in window)) {
                                alert("Tu navegador no soporta lectura de texto.");
                                return;
                            }
                            const utterance = new SpeechSynthesisUtterance(rawTargetText);
                            utterance.lang = moduloactual.cursos_item.html || "en-US";
                            let voices = window.speechSynthesis.getVoices();
                            if (voices.length === 0) {
                                // Sometimes Chrome requires a delay to load voices
                                setTimeout(() => {
                                    voices = window.speechSynthesis.getVoices();
                                    playWithVoices(voices);
                                }, 50);
                            } else {
                                playWithVoices(voices);
                            }

                            function playWithVoices(loadedVoices) {
                                const lang = (moduloactual.cursos_item.html || "en-US").toLowerCase();
                                const primaryLang = lang.split("-")[0]; // e.g. "en" from "en-us"

                                // 1. Filter voices by exact language match, or primary language match
                                let langVoices = loadedVoices.filter(v => v.lang.toLowerCase() === lang);
                                if (langVoices.length === 0) {
                                    langVoices = loadedVoices.filter(v => v.lang.toLowerCase().startsWith(primaryLang));
                                }
                                if (langVoices.length === 0) {
                                    langVoices = loadedVoices; // Fallback to all voices
                                }

                                // 2. Prioritize premium/natural/high-quality voices (Google, Natural, Siri, Apple, Microsoft)
                                let selectedVoice = langVoices.find(v => {
                                    const name = v.name.toLowerCase();
                                    return name.includes("natural") || name.includes("google") || name.includes("siri") || name.includes("apple");
                                });

                                // Priority 2: Microsoft voices that are not David or Zira
                                if (!selectedVoice) {
                                    selectedVoice = langVoices.find(v => {
                                        const name = v.name.toLowerCase();
                                        return name.includes("microsoft") && !name.includes("zira") && !name.includes("david");
                                    });
                                }

                                // Priority 3: Any voice that is not Zira or David
                                if (!selectedVoice) {
                                    selectedVoice = langVoices.find(v => {
                                        const name = v.name.toLowerCase();
                                        return !name.includes("zira") && !name.includes("david");
                                    });
                                }

                                // Priority 4: Fallback to the first available voice for the language
                                if (!selectedVoice && langVoices.length > 0) {
                                    selectedVoice = langVoices[0];
                                }

                                if (selectedVoice) {
                                    utterance.voice = selectedVoice;
                                }
                                utterance.rate = 0.75; // Slower rate so it is clearer and not too fast
                                window.speechSynthesis.cancel();
                                window.speechSynthesis.speak(utterance);
                            }
                        }}
                        style={{ width: "60px", height: "60px" }}
                        title="Escuchar pronunciación"
                    >
                        <VolumeHighOutline color={"#17a2b8"} height="30px" width="30px" />
                    </button>

                    {!isListening ? (
                        <button
                            className="btn btn-primary rounded-circle p-3 d-flex justify-content-center align-items-center mr-3"
                            onClick={toggleListening}
                            style={{ width: "70px", height: "70px" }}
                        >
                            <MicOutline color={"#fff"} height="35px" width="35px" />
                        </button>
                    ) : (
                        <button
                            className="btn btn-danger rounded-circle p-3 d-flex justify-content-center align-items-center mr-3"
                            onClick={toggleListening}
                            style={{ width: "70px", height: "70px" }}
                        >
                            <Stop color={"#fff"} height="35px" width="35px" />
                        </button>
                    )}

                    <button
                        className="btn btn-outline-secondary rounded-circle p-3 d-flex justify-content-center align-items-center"
                        onClick={resetPractice}
                        style={{ width: "50px", height: "50px" }}
                        title="Reiniciar"
                    >
                        <RefreshOutline color={"#6c757d"} height="25px" width="25px" />
                    </button>
                </div>

                <div className="mt-4">
                    <h5>
                        Precisión: <span className={score >= 80 ? "text-success" : "text-primary"}>{score}%</span>
                    </h5>
                    <div className="progress mx-auto" style={{ height: "10px", width: "50%" }}>
                        <div 
                            className={`progress-bar ${score >= 80 ? "bg-success" : "bg-primary"}`} 
                            role="progressbar" 
                            style={{ width: `${score}%`, transition: "width 0.5s" }} 
                            aria-valuenow={score} 
                            aria-valuemin="0" 
                            aria-valuemax="100"
                        ></div>
                    </div>
                </div>

                {hasPassed && (
                    <div className="alert alert-success mt-4 d-inline-block">
                        <CheckmarkCircle color="green" height="25px" width="25px" className="mr-2 mb-1" />
                        ¡Excelente pronunciación! Has completado esta actividad.
                        {moduloactual.hecho ? "" : " (Guardando tu progreso...)"}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PronunciationPractice;
